import { BANDS, normalizeBand } from './studioLabels.js';
import { isStudioDraftEmpty, normalizeStudioDraft } from '../services/studioDrafts.js';

/** Chiave filtro / sezione lista per bozze senza slot Studio. */
export const UNASSIGNED_STUDIO_SLOT_KEY = 'unslotted';

/** Ruoli Mantra usati da Studio (budget e conteggi). */
export const MANTRA_ROLES = ['P', 'DS', 'DC', 'DD', 'B', 'E', 'M', 'C', 'W', 'T', 'A', 'PC'];

/** Alias listone / legacy → ruolo Mantra (unificato con filtri Svincolati). */
const LEGACY_MANTRA_ALIASES = {
  POR: 'P',
  MC: 'C',
  MS: 'C',
  MD: 'C',
  AC: 'A',
  AS: 'A',
  AD: 'A',
};

export function parseMantraRoles(rawRole) {
  return canonicalMantraRoles(
    String(rawRole || '')
      .split(/[^A-Za-z]+/)
      .filter(Boolean),
  );
}

/** Ruoli Mantra di un giocatore in lista/Studio (stesso criterio dei filtri Studio). */
export function resolveStudioPlayerMantraRoles(player) {
  if (!player) return [];
  if (Array.isArray(player.roles)) {
    const parts = player.roles.map((r) => String(r ?? '').trim()).filter(Boolean);
    if (parts.length) return parseMantraRoles(parts.join(';'));
  }
  if (player.role) return parseMantraRoles(player.role);
  return [];
}

/** Normalizza alias listone (es. Por/POR → P, MC → C). */
export function canonicalMantraRole(raw) {
  const u = String(raw ?? '').trim().toUpperCase();
  if (!u) return null;
  if (LEGACY_MANTRA_ALIASES[u]) return LEGACY_MANTRA_ALIASES[u];
  return MANTRA_ROLES.includes(u) ? u : null;
}

/** Ruoli Mantra unici, ordinati come in input. */
export function canonicalMantraRoles(rolesOrRaw) {
  if (rolesOrRaw == null) return [];
  const list = Array.isArray(rolesOrRaw)
    ? rolesOrRaw
    : String(rolesOrRaw).split(/[^A-Za-z]+/);
  const out = [];
  for (const r of list) {
    const c = canonicalMantraRole(r);
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * Calcola il piano di spesa dello Studio (ripartizione per ruolo Mantra).
 * @param {Array<{maxPrice:number, role?:string}>} targetList
 * @param {number} total budget totale
 * @returns {{allocated:number, margin:number, over:boolean, byRole:Record<string,{count:number,allocated:number}>}}
 */
export function computeBudget(targetList, total) {
  const byRole = Object.fromEntries(MANTRA_ROLES.map((r) => [r, { count: 0, allocated: 0 }]));
  let allocated = 0;
  for (const item of targetList) {
    // Escludi target usciti/presi nell'asta attiva
    if (item.gone === true || item.sold === true) continue;
    const maxPrice = Number(item.maxPrice) || 0;
    allocated += maxPrice;
    const roles = parseMantraRoles(item.role);
    const firstMantraRole = roles.find((r) => MANTRA_ROLES.includes(r));
    if (!firstMantraRole) continue;
    byRole[firstMantraRole].count += 1;
    byRole[firstMantraRole].allocated += maxPrice;
  }
  const tot = Number(total) || 0;
  return { allocated, margin: tot - allocated, over: allocated > tot, byRole };
}

function sortOrderOf(target) {
  const n = Number(target?.sortOrder);
  return Number.isFinite(n) ? n : 0;
}

function compareSlotItems(a, b) {
  const d = sortOrderOf(a[1]) - sortOrderOf(b[1]);
  if (d !== 0) return d;
  return String(a[0]).localeCompare(String(b[0]));
}

/**
 * Raggruppa le entry [playerKey, target] per slot 1–8, nell'ordine di BANDS,
 * escludendo gli slot senza elementi. Dentro ogni slot ordina per sortOrder.
 */
export function groupByBand(entries) {
  return BANDS.map(({ key, label }) => ({
    key,
    label,
    items: entries
      .filter(([, t]) => normalizeBand(t.band) === key)
      .slice()
      .sort(compareSlotItems),
  })).filter((g) => g.items.length > 0);
}

/**
 * Bozze Studio (tag/prezzo/note) non ancora promosse a target con slot.
 * Esclude chiavi già in studioTargets e bozze vuote.
 */
export function listStudioDraftEntries(studioDrafts, studioTargets) {
  if (!(studioDrafts instanceof Map)) return [];
  const targets = studioTargets instanceof Map ? studioTargets : new Map();
  const out = [];
  for (const [key, draft] of studioDrafts.entries()) {
    if (targets.has(key)) continue;
    const normalized = normalizeStudioDraft(draft);
    if (isStudioDraftEmpty(normalized)) continue;
    out.push([key, normalized]);
  }
  return out.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
}

/**
 * Filtra entry Studio [playerKey, target] con criteri AND (ruolo, etichetta, squadra, opz. slot).
 * @param {boolean} [options.useBand=true] — bozze senza slot ignorano il filtro band
 */
export function filterStudioEntries(entries, filters = {}, resolvePlayer = () => undefined, { useBand = true } = {}) {
  const role = filters.role ? String(filters.role).toUpperCase() : null;
  const label = filters.label || null;
  const team = filters.team ? String(filters.team).trim().toLowerCase() : null;
  const bandRaw = useBand && filters.band ? String(filters.band) : null;
  const band = bandRaw === UNASSIGNED_STUDIO_SLOT_KEY
    ? UNASSIGNED_STUDIO_SLOT_KEY
    : (bandRaw ? normalizeBand(bandRaw) : null);

  return entries.filter(([playerKey, target]) => {
    if (band === UNASSIGNED_STUDIO_SLOT_KEY) return false;
    if (band && normalizeBand(target.band) !== band) return false;
    if (label && !(target.labels || []).includes(label)) return false;

    const player = resolvePlayer(playerKey);
    if (team) {
      const pTeam = String(player?.team || '').trim().toLowerCase();
      if (pTeam !== team) return false;
    }
    if (role) {
      const roles = parseMantraRoles(playerRoleRaw(player));
      if (!roles.includes(role)) return false;
    }
    return true;
  });
}

/** Filtra bozze senza slot (stessi criteri ruolo/etichetta/squadra dei target, senza band). */
export function filterStudioDraftEntries(entries, filters = {}, resolvePlayer = () => undefined) {
  return filterStudioEntries(entries, filters, resolvePlayer, { useBand: false });
}

/**
 * Somma i maxPrice di un gruppo slot `[[playerKey, target], ...]`.
 * Esclude target usciti/presi come `computeBudget` (sold/gone o callback isGone).
 */
export function sumSlotAllocated(items, { isGone } = {}) {
  let sum = 0;
  for (const entry of items || []) {
    const playerKey = entry?.[0];
    const t = entry?.[1];
    if (t?.gone === true || t?.sold === true) continue;
    if (isGone?.(playerKey, t)) continue;
    sum += Number(t?.maxPrice) || 0;
  }
  return sum;
}

const OTHER_ROLE_KEY = 'Altri';

function playerRoleRaw(player) {
  if (Array.isArray(player?.roles) && player.roles.length) {
    return player.roles.join(';');
  }
  return player?.role || '';
}

/**
 * Dentro uno slot, raggruppa le entry per ruolo Mantra.
 * Multi-ruolo: la stessa entry compare in ogni ruolo Mantra posseduto.
 * Senza ruolo Mantra → gruppo "Altri". Gruppi vuoti esclusi.
 *
 * @param {Array<[string, object]>} items
 * @param {(playerKey: string) => { role?: string, roles?: string[] }|undefined} resolvePlayer
 * @returns {Array<{ key: string, label: string, items: Array<[string, object]> }>}
 */
export function groupSlotItemsByMantraRole(items, resolvePlayer = () => undefined) {
  const buckets = Object.fromEntries(MANTRA_ROLES.map((r) => [r, []]));
  const others = [];

  for (const entry of items || []) {
    const [playerKey] = entry;
    const player = resolvePlayer(playerKey);
    const mantraRoles = parseMantraRoles(playerRoleRaw(player)).filter((r) =>
      MANTRA_ROLES.includes(r),
    );
    if (mantraRoles.length === 0) {
      others.push(entry);
      continue;
    }
    for (const role of mantraRoles) {
      buckets[role].push(entry);
    }
  }

  const groups = MANTRA_ROLES.map((key) => ({
    key,
    label: key,
    items: buckets[key].slice().sort(compareSlotItems),
  })).filter((g) => g.items.length > 0);

  if (others.length > 0) {
    groups.push({
      key: OTHER_ROLE_KEY,
      label: OTHER_ROLE_KEY,
      items: others.slice().sort(compareSlotItems),
    });
  }

  return groups;
}


/** Squadre uniche presenti tra i target (ordinate), via resolvePlayer. */
export function collectStudioTeams(entries, resolvePlayer = () => undefined) {
  const set = new Set();
  for (const [playerKey] of entries) {
    const team = String(resolvePlayer(playerKey)?.team || '').trim();
    if (team) set.add(team);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
}

/**
 * Conteggio target Studio per facet filtro (ruolo / etichetta / squadra / slot).
 * Un giocatore multi-ruolo incrementa ogni ruolo Mantra che possiede.
 */
export function countStudioFilterFacets(entries, resolvePlayer = () => undefined, draftEntries = []) {
  const roles = Object.fromEntries(MANTRA_ROLES.map((r) => [r, 0]));
  const bands = Object.fromEntries(BANDS.map(({ key }) => [key, 0]));
  const labels = Object.create(null);
  const teams = Object.create(null);
  let unslotted = 0;

  const bumpEntry = (playerKey, target) => {
    const player = resolvePlayer(playerKey);
    for (const r of resolveStudioPlayerMantraRoles(player)) {
      if (Object.prototype.hasOwnProperty.call(roles, r)) roles[r] += 1;
    }
    for (const l of target.labels || []) {
      labels[l] = (labels[l] || 0) + 1;
    }
    const team = String(player?.team || '').trim();
    if (team) teams[team] = (teams[team] || 0) + 1;
  };

  for (const [playerKey, target] of entries) {
    const band = normalizeBand(target.band);
    if (Object.prototype.hasOwnProperty.call(bands, band)) bands[band] += 1;
    bumpEntry(playerKey, target);
  }

  for (const [playerKey, target] of draftEntries) {
    unslotted += 1;
    bumpEntry(playerKey, target);
  }

  return { roles, labels, teams, bands, unslotted };
}
